import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Mail, Sparkles } from "lucide-react";
import { triggerSubscription } from "@/lib/subscription";

export default function SubscribeDialog({ open, onOpenChange, trigger }) {
  const [form, setForm] = useState({
    nom: "", prenom: "", email: "", telephone: "",
    entreprise: "", siret: "",
    adresse: "", code_postal: "", ville: "", pays: "France",
  });

  const submit = (e) => {
    e.preventDefault();
    triggerSubscription(form);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger}
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            Souscription PAG Business Manager
          </DialogTitle>
          <DialogDescription>
            Renseignez vos coordonnées — nous générons automatiquement votre <b>bulletin PDF</b> et ouvrons votre client email avec un <b>message pré-rempli</b> adressé à contact@parcavenue.eu.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5" data-testid="subscribe-form">
          <div>
            <SectionTitle>1. Vos coordonnées</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <F label="Nom" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} testid="sub-nom" required />
              <F label="Prénom" value={form.prenom} onChange={(v) => setForm({ ...form, prenom: v })} testid="sub-prenom" required />
              <F label="Email pro" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="sub-email" required />
              <F label="Téléphone pro" type="tel" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} testid="sub-telephone" required />
            </div>
          </div>

          <div>
            <SectionTitle>2. Votre entreprise</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <F label="Raison sociale" value={form.entreprise} onChange={(v) => setForm({ ...form, entreprise: v })} testid="sub-entreprise" required />
              </div>
              <F label="SIRET / SIREN" value={form.siret} onChange={(v) => setForm({ ...form, siret: v })} testid="sub-siret" required />
              <F label="Pays" value={form.pays} onChange={(v) => setForm({ ...form, pays: v })} testid="sub-pays" />
              <div className="col-span-2">
                <F label="Adresse" value={form.adresse} onChange={(v) => setForm({ ...form, adresse: v })} testid="sub-adresse" required />
              </div>
              <F label="Code postal" value={form.code_postal} onChange={(v) => setForm({ ...form, code_postal: v })} testid="sub-cp" required />
              <F label="Ville" value={form.ville} onChange={(v) => setForm({ ...form, ville: v })} testid="sub-ville" required />
            </div>
          </div>

          <div className="p-4 border border-[#002FA7] bg-[#F8F9FA] flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-[#002FA7] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-[#495057]">
              <div className="font-display font-bold text-[#002FA7] mb-1">Ce qui se passe en cliquant sur "Envoyer ma souscription"</div>
              <ol className="space-y-0.5 list-decimal list-inside">
                <li>Téléchargement automatique du <b>bulletin PDF</b> pré-rempli avec vos infos.</li>
                <li>Ouverture de votre <b>client email</b> avec un message pré-rempli vers <span className="font-mono">contact@parcavenue.eu</span>.</li>
                <li>Vous n'avez qu'à <b>joindre le PDF</b> téléchargé, signer si nécessaire, et envoyer.</li>
              </ol>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} data-testid="sub-cancel">
              Annuler
            </Button>
            <Button type="submit" data-testid="sub-submit" className="bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
              <Download className="h-4 w-4 mr-2" />
              <Mail className="h-4 w-4 mr-2" />
              Envoyer ma souscription
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#0A0A0A] mb-3 flex items-center gap-2">
      <span className="h-px bg-[#0A0A0A] w-6" />{children}
    </div>
  );
}

function F({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">{label}{required && <span className="text-[#E60000]"> *</span>}</Label>
      <Input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 border-[#DEE2E6] focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
        required={required}
      />
    </div>
  );
}
